import { useMemo } from 'react';
import StatusTabs from './StatusTabs';
import DeviceTable from './DeviceTable';
import S from './BroadcastQueueStyles';
import type { DeviceRow, StatusTab } from './types';
import { QUEUED_TAB_ID, statusToTabId } from './constants';

type SearchResults = { rows: DeviceRow[]; total: number };

type Props = {
  fleetId?: string | null;
  tabs: StatusTab[];
  devices: DeviceRow[];
  activeTabId: string;
  onTabChange: (tabId: string) => void;
  selectedDeviceIds?: string[];
  onSelectionChange?: (deviceId: string, checked: boolean) => void;
  searchResults?: SearchResults | null;
  isSearchActive?: boolean;
  isSearchLoading?: boolean;
};

export default function BroadcastQueueItemBody({
  fleetId = null,
  tabs,
  devices,
  activeTabId,
  onTabChange,
  selectedDeviceIds = [],
  onSelectionChange,
  searchResults = null,
  isSearchActive = false,
  isSearchLoading = false,
}: Props): JSX.Element {
  const activeTab = tabs.find((t) => t.id === activeTabId);
  const filteredRows = useMemo((): DeviceRow[] => {
    if (searchResults != null) {
      return searchResults.rows;
    }
    if (!activeTab) return devices;
    return devices.filter(
      (d) => statusToTabId(d.messageStatus) === activeTab.id,
    );
  }, [devices, activeTab, searchResults]);

  const hasQueuedInSearchResults = useMemo(
    () => searchResults != null
      && filteredRows.some((r) => statusToTabId(r.messageStatus) === QUEUED_TAB_ID),
    [searchResults, filteredRows],
  );

  const showCheckbox = (activeTabId === QUEUED_TAB_ID && searchResults == null)
    || hasQueuedInSearchResults;
  const showCheckboxOnlyForQueued = searchResults != null;
  const showSearchHeader = isSearchActive || searchResults != null;

  return (
    <S.ItemBody>
      {showSearchHeader ? (
        <S.SearchResultsLabel>
          {isSearchLoading ? 'Searching…' : `Search results (${searchResults?.total ?? 0})`}
        </S.SearchResultsLabel>
      ) : (
        <StatusTabs
          tabs={tabs}
          activeTabId={activeTabId}
          onTabChange={onTabChange}
        />
      )}
      <DeviceTable
        fleetId={fleetId}
        rows={filteredRows}
        showCheckbox={showCheckbox}
        showCheckboxOnlyForQueued={showCheckboxOnlyForQueued}
        selectedDeviceIds={selectedDeviceIds}
        onSelectionChange={onSelectionChange}
      />
    </S.ItemBody>
  );
}
